/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */

import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';

import { CustomPopover } from '@futrli/ui-components';

import { Select as CarbonSelect, Option } from 'carbon-react/lib/components/select';
import Icon from 'carbon-react/lib/components/icon';
import { DatePicker } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

import { useReportBuilderContext } from '../../../report-builder.context';
import { PERIOD_END_OPTIONS, CARD_TYPES, LayoutData, PERIOD_OPTIONS } from '../../../types';
import { DATE_FORMAT, FETCH_DATA_DELAY } from '../../../constants';
import { getCalendarOptionsFromEndDateOption } from '../../../utils-dates';
import { useI18nConstants } from '../../../use-i18n-constants';

import styles from './period-end-dropdown.scss';

export const PeriodEndDropdown = () => {
  const intl = useIntl();
  const { pageLayoutData, setPageLayoutData, setIsPendingNewFinData, setCurrentCardId } = useReportBuilderContext();
  const { periodEndOptions } = useI18nConstants();

  const periodChangeRef = useRef<HTMLDivElement>(null);

  const [customDate, setCustomDate] = useState(format(new Date(), DATE_FORMAT));
  const [pendingEndDateUpdate, setPendingEndDateUpdate] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<PERIOD_END_OPTIONS>(PERIOD_END_OPTIONS.CUSTOM_DATES);

  const handleDateChange = (date: unknown) => {
    if (!date) {
      return;
    }
    const formatted = (date as Dayjs).format('YYYY-MM-DD');
    setCustomDate(formatted);
    if (selectedPeriod === 'custom') {
      setCurrentCardId('');
      setPendingEndDateUpdate(true);
    }
    handlePopoverClose();
  };

  const handleOpenDatePicker = () => setOpenDatePicker(true);
  const handleCloseDatePicker = () => setOpenDatePicker(false);

  const handlePopoverClose = () => {
    setPopoverOpen(false);
  };

  const handleSelectedPeriodChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    setSelectedPeriod(event.target.value as PERIOD_END_OPTIONS);
    if (
      ![PERIOD_END_OPTIONS.CUSTOM, PERIOD_END_OPTIONS.MIXED, PERIOD_END_OPTIONS.CUSTOM_DATES].includes(
        event.target.value as PERIOD_END_OPTIONS
      )
    ) {
      setCurrentCardId('');
      setPendingEndDateUpdate(true);
    }
    handleSelectClose();
  };

  const handleSelectClose = () => {
    setTimeout(() => {
      if (periodChangeRef.current?.getElementsByTagName('input')?.[0]?.value.includes('Custom')) {
        setPopoverOpen(true);
      }
    }, 200);
  };

  // set correct calendar options on every widget if end date has changed
  useEffect(() => {
    if (pendingEndDateUpdate) {
      setPendingEndDateUpdate(false);
      const selectedPeriodDetails = periodEndOptions.find(p => p.value === selectedPeriod);
      if (
        !selectedPeriodDetails ||
        selectedPeriodDetails.value === PERIOD_END_OPTIONS.CUSTOM_DATES ||
        selectedPeriodDetails.value === PERIOD_END_OPTIONS.MIXED
      ) {
        return;
      }
      const newPageLayoutData = pageLayoutData.map(pld => {
        if (pld.type === CARD_TYPES.WIDGET) {
          return {
            ...pld,
            [CARD_TYPES.WIDGET]: {
              ...pld[CARD_TYPES.WIDGET],
              selectedCalFields: [
                {
                  ref: selectedPeriodDetails.type,
                  name: selectedPeriodDetails.typeName,
                  classifier: ''
                }
              ],
              calendarOptions: {
                ...getCalendarOptionsFromEndDateOption(
                  selectedPeriodDetails,
                  customDate,
                  pld[CARD_TYPES.WIDGET]?.calendarOptions
                ),
                type: selectedPeriodDetails.type
              }
            }
          };
        }
        if (
          pld.type === CARD_TYPES.APP_WIDGET &&
          [
            PERIOD_END_OPTIONS.THIS_MONTH,
            PERIOD_END_OPTIONS.LAST_MONTH,
            PERIOD_END_OPTIONS.THIS_QUARTER,
            PERIOD_END_OPTIONS.LAST_QUARTER,
            PERIOD_END_OPTIONS.THIS_FIN_YEAR,
            PERIOD_END_OPTIONS.LAST_FIN_YEAR
          ].includes(selectedPeriod)
        ) {
          return {
            ...pld,
            [CARD_TYPES.APP_WIDGET]: {
              ...pld[CARD_TYPES.APP_WIDGET],
              period: selectedPeriod
            }
          };
        }
        return pld;
      });
      setPageLayoutData(newPageLayoutData as LayoutData[]);
      setTimeout(() => setIsPendingNewFinData(true), FETCH_DATA_DELAY);
    }
  }, [
    selectedPeriod,
    customDate,
    pendingEndDateUpdate,
    pageLayoutData,
    setPageLayoutData,
    setIsPendingNewFinData,
    periodEndOptions
  ]);

  // setting the default Period End value based on calendarOptions for all cards
  useEffect(() => {
    let type = '';
    let date = '';
    let isMixed = false;
    let defaultOption = PERIOD_END_OPTIONS.CUSTOM_DATES;
    let defaultDate = format(new Date(), DATE_FORMAT);
    pageLayoutData.some(pld => {
      if (pld.type === CARD_TYPES.WIDGET && pld[CARD_TYPES.WIDGET]?.calendarOptions) {
        if (
          (type && type !== pld[CARD_TYPES.WIDGET]?.calendarOptions?.type) ||
          (date && date !== pld[CARD_TYPES.WIDGET]?.calendarOptions?.startDate)
        ) {
          if (type) {
            isMixed = true;
          }
          type = '';
          date = '';
          return true;
        } else {
          type = pld[CARD_TYPES.WIDGET]?.calendarOptions?.type || '';
          date = pld[CARD_TYPES.WIDGET]?.calendarOptions?.startDate || '';
        }
      }
      return false;
    });
    if (date !== PERIOD_OPTIONS.THIS_PERIOD && date !== PERIOD_OPTIONS.LAST_PERIOD) {
      if (type === 'monthly') {
        defaultOption = PERIOD_END_OPTIONS.CUSTOM;
        defaultDate = date;
      } else {
        defaultOption = isMixed ? PERIOD_END_OPTIONS.MIXED : PERIOD_END_OPTIONS.CUSTOM_DATES;
      }
    } else {
      if (type === 'daily') {
        defaultOption = date === PERIOD_OPTIONS.THIS_PERIOD ? PERIOD_END_OPTIONS.TODAY : PERIOD_END_OPTIONS.YESTERDAY;
      }
      if (type === 'monthly') {
        defaultOption =
          date === PERIOD_OPTIONS.THIS_PERIOD ? PERIOD_END_OPTIONS.THIS_MONTH : PERIOD_END_OPTIONS.LAST_MONTH;
      }
      if (type === 'weekly_mon') {
        defaultOption =
          date === PERIOD_OPTIONS.THIS_PERIOD ? PERIOD_END_OPTIONS.THIS_WEEK_MON : PERIOD_END_OPTIONS.LAST_WEEK_MON;
      }
      if (type === 'weekly_sun') {
        defaultOption =
          date === PERIOD_OPTIONS.THIS_PERIOD ? PERIOD_END_OPTIONS.THIS_WEEK_SUN : PERIOD_END_OPTIONS.LAST_WEEK_SUN;
      }
      if (type === 'quarterly') {
        defaultOption =
          date === PERIOD_OPTIONS.THIS_PERIOD ? PERIOD_END_OPTIONS.THIS_QUARTER : PERIOD_END_OPTIONS.LAST_QUARTER;
      }
      if (type === 'yearly') {
        defaultOption =
          date === PERIOD_OPTIONS.THIS_PERIOD ? PERIOD_END_OPTIONS.THIS_FIN_YEAR : PERIOD_END_OPTIONS.LAST_FIN_YEAR;
      }
    }
    setSelectedPeriod(defaultOption);
    setCustomDate(defaultDate);
  }, [pageLayoutData, setCurrentCardId]);

  return (
    <div>
      <div className={clsx(styles.selectContainer)} ref={periodChangeRef}>
        <CarbonSelect
          labelId="periodEnd"
          label={intl.formatMessage({
            id: 'common.period_end',
            defaultMessage: 'Period end:'
          })}
          labelInline
          id="periodEndSelect"
          onChange={handleSelectedPeriodChange}
          value={selectedPeriod}
          autoComplete="off"
          listPlacement="bottom-end"
          style={{ minWidth: 210, marginLeft: 10 }}
          listMaxHeight={periodEndOptions.length * 40}
        >
          {periodEndOptions.map(period => (
            <Option
              key={period.name}
              value={period.value}
              text={period.name}
              disabled={period.value === PERIOD_END_OPTIONS.CUSTOM_DATES || period.value === PERIOD_END_OPTIONS.MIXED}
            />
          ))}
        </CarbonSelect>
      </div>

      <CustomPopover
        open={popoverOpen}
        anchorEl={periodChangeRef.current}
        onClose={handlePopoverClose}
        id="custom-date-select-popover"
        offset={{ x: 0, y: 11 }}
        style={{ width: 210 }}
      >
        <div className={styles.datePickerMui}>
          <DatePicker
            onChange={handleDateChange}
            onAccept={handleDateChange}
            inputFormat="MMMM YYYY"
            value={customDate ? dayjs(customDate) : dayjs()}
            views={['year', 'month']}
            openTo="month"
            open={openDatePicker}
            onOpen={handleOpenDatePicker}
            onClose={handleCloseDatePicker}
            renderInput={params => (
              <TextField {...params} onClick={handleOpenDatePicker} style={{ cursor: 'pointer' }} />
            )}
            InputProps={{
              endAdornment: (
                <button className={styles.transparentBtn}>
                  <Icon type="calendar" />
                </button>
              )
            }}
          />
        </div>
      </CustomPopover>
    </div>
  );
};
